import { useState, useEffect, useRef } from 'react';
import type React from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { AutoComplete, Button, Card, Checkbox, DatePicker, Descriptions, Form, Input, Modal, Select, Space, Steps, Typography, Alert } from 'antd';
import type { InputRef } from 'antd';
import { MobileOutlined, LoginOutlined, QuestionCircleOutlined, DisconnectOutlined, ExclamationCircleFilled, EditOutlined, CheckCircleOutlined, SolutionOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  sendOtp,
  verifyOtp,
  recoverAccountEmail,
  unlinkAccountPhone,
  saveGuestDemographics,
  getGuestProfile,
  type GuestDemographics,
  type GuestProfile,
  type VerifyOtpResult,
} from '../../services/guestAuth.service';
import { useGuestSession } from '../../services/useGuestSession';
import { Logo } from '../../components/common/Logo';
import { profileService } from '../../services/profile.service';
import type { City, State } from '../../types/profile.types';
import { AgeRange, calculateAgeRange, getAgeRangeLabel } from '../../utils/age-range';
import { EconomicLevelInfoIcon } from '../../components/common/EconomicLevelInfo';

const MINIMUM_PARTICIPANT_AGE = 16;
const MAXIMUM_PARTICIPANT_AGE = 133;

const { Title, Text, Paragraph } = Typography;

const PHONE_E164_REGEX = /^\+55\d{2}\d{8,9}$/;

function parseE164(value: string | undefined): { ddd: string; number: string } {
  if (!value || !value.startsWith('+55')) return { ddd: '', number: '' };
  const digits = value.slice(3);
  return { ddd: digits.slice(0, 2), number: digits.slice(2) };
}

function buildE164(ddd: string, number: string): string {
  const raw = ddd + number;
  return raw ? '+55' + raw : '';
}

function applyNumberMask(raw: string): string {
  if (raw.length <= 4) return raw;
  if (raw.length <= 8) return raw.slice(0, 4) + '-' + raw.slice(4);
  return raw.slice(0, 5) + '-' + raw.slice(5, 9);
}

interface PhoneInputProps {
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
}

const PhoneInput: React.FC<PhoneInputProps> = ({ value, onChange, disabled }) => {
  const parsed = parseE164(value);
  const [ddd, setDdd] = useState(parsed.ddd);
  const [number, setNumber] = useState(parsed.number);
  const numberInputRef = useRef<InputRef>(null);

  useEffect(() => {
    const p = parseE164(value);
    if (p.ddd !== ddd) setDdd(p.ddd);
    if (p.number !== number) setNumber(p.number);
  }, [value, ddd, number]);

  const handleDddChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 2);
    setDdd(raw);
    onChange?.(buildE164(raw, number));
    if (raw.length === 2) numberInputRef.current?.focus();
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 9);
    setNumber(raw);
    onChange?.(buildE164(ddd, raw));
  };

  return (
    <Space.Compact style={{ width: '100%' }}>
      <Input
        value="🇧🇷 +55"
        readOnly
        disabled={disabled}
        size="large"
        tabIndex={-1}
        style={{ width: '90px', flexShrink: 0, cursor: 'default', color: 'rgba(0,0,0,0.88)' }}
      />
      <Input
        value={ddd}
        onChange={handleDddChange}
        placeholder="47"
        maxLength={2}
        disabled={disabled}
        size="large"
        inputMode="numeric"
        aria-label="DDD"
        style={{ width: '68px', flexShrink: 0 }}
      />
      <Input
        ref={numberInputRef}
        value={applyNumberMask(number)}
        onChange={handleNumberChange}
        placeholder="99999-9999"
        disabled={disabled}
        size="large"
        inputMode="numeric"
        aria-label="Número de telefone"
        style={{ flex: 1 }}
      />
    </Space.Compact>
  );
};

type Step = 'phone' | 'otp' | 'accountConflict' | 'confirmProfile' | 'profile';

const GENDER_OPTIONS = [
  { value: 'MALE', label: 'Masculino' },
  { value: 'FEMALE', label: 'Feminino' },
];

const EDUCATION_OPTIONS = [
  { value: 'FUNDAMENTAL', label: 'Ensino Fundamental' },
  { value: 'MEDIO', label: 'Ensino Médio' },
  { value: 'SUPERIOR', label: 'Ensino Superior' },
  { value: 'POS_GRADUACAO', label: 'Pós-Graduação' },
  { value: 'MESTRADO', label: 'Mestrado' },
  { value: 'DOUTORADO', label: 'Doutorado' },
];

const ECONOMIC_OPTIONS = [
  { value: 'NO_INCOME', label: 'Sem renda' },
  { value: 'UP_TO_HALF_MW', label: 'Até ½ salário mínimo' },
  { value: 'ONE_TO_TWO_MW', label: '1 a 2 salários mínimos' },
  { value: 'TWO_TO_FIVE_MW', label: '2 a 5 salários mínimos' },
  { value: 'FIVE_TO_TEN_MW', label: '5 a 10 salários mínimos' },
  { value: 'TEN_TO_TWENTY_MW', label: '10 a 20 salários mínimos' },
  { value: 'ABOVE_TWENTY_MW', label: 'Acima de 20 salários mínimos' },
];

export default function SurveyPhoneOtpPage() {
  const { surveyHash } = useParams<{ surveyHash: string }>();
  const location = useLocation();
  const surveyId: string = location.state?.surveyId ?? surveyHash ?? '';
  const navigate = useNavigate();
  const session = useGuestSession();

  const [step, setStep] = useState<Step>('phone');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phone, setPhone] = useState('');
  const [lgpdConsent, setLgpdConsent] = useState(false);
  const [challengeId, setChallengeId] = useState('');
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [resendAvailableAt, setResendAvailableAt] = useState<Date | null>(null);
  const [savedToken, setSavedToken] = useState('');
  const [emailRecoverySent, setEmailRecoverySent] = useState(false);
  const [unlinkLoading, setUnlinkLoading] = useState(false);
  const [profile, setProfile] = useState<GuestProfile | null>(null);

  // Demographics state
  const [gender, setGender] = useState<string>('');
  const [dateOfBirth, setDateOfBirth] = useState<string>('');
  const [educationLevel, setEducationLevel] = useState<string>('');
  const [economicLevel, setEconomicLevel] = useState<string>('');
  const [selectedAgeRange, setSelectedAgeRange] = useState<AgeRange | null>(null);

  // Localização
  const [states, setStates] = useState<State[]>([]);
  const [selectedStateId, setSelectedStateId] = useState<string>('');
  const [cities, setCities] = useState<City[]>([]);
  const [searchingCities, setSearchingCities] = useState(false);
  const [citySearchText, setCitySearchText] = useState('');
  const [cityInputValue, setCityInputValue] = useState('');
  const [cityId, setCityId] = useState('');
  const [neighborhood, setNeighborhood] = useState('');

  const fingerprint = (window as any).__fpjs_fingerprint ?? Math.random().toString(36).slice(2);

  useEffect(() => {
    profileService.getStates().then(setStates).catch(() => {
      // Lista de estados é auxiliar; falha aqui não bloqueia o restante do formulário.
    });
  }, []);

  useEffect(() => {
    if (!selectedStateId) return;
    if (citySearchText.length > 0 && citySearchText.length < 2) return;

    const timer = setTimeout(() => {
      setSearchingCities(true);
      profileService
        .getCitiesByState(selectedStateId, citySearchText)
        .then(setCities)
        .catch(() => setCities([]))
        .finally(() => setSearchingCities(false));
    }, 300);

    return () => clearTimeout(timer);
  }, [selectedStateId, citySearchText]);

  function handleStateChange(newStateId: string) {
    setSelectedStateId(newStateId);
    setCityId('');
    setCityInputValue('');
    setCitySearchText('');
    setCities([]);
  }

  function handleCitySelect(_value: string, option: { id?: string; label?: string }) {
    if (option.id) setCityId(option.id);
    if (option.label) setCityInputValue(option.label);
  }

  async function handleSendOtp() {
    if (!lgpdConsent) {
      setError('Você precisa aceitar o uso do telefone para continuar.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const result = await sendOtp(phone, surveyId!, fingerprint);
      setChallengeId(result.challengeId);
      setResendAvailableAt(new Date(result.resendAvailableAt));
      setStep('otp');
    } catch (err: any) {
      setError(err.response?.data?.message ?? 'Erro ao enviar o código. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  async function proceedAsGuest(result: Extract<VerifyOtpResult, { token: string }>) {
    session.save(result.token, result.guestParticipantId, result.expiresAt);
    setSavedToken(result.token);
    if (result.isNewParticipant) {
      setStep('profile');
      return;
    }

    setError(null);
    setLoading(true);
    try {
      const fetchedProfile = await getGuestProfile(result.token);
      setProfile(fetchedProfile);
      if (!fetchedProfile.hasCompletedProfile) {
        // Participante antigo sem perfil completo (ex: criado antes do passo de demografia existir).
        setStep('profile');
        return;
      }
      setDisplayName(fetchedProfile.displayName ?? '');
      setStep('confirmProfile');
    } catch {
      // Não foi possível carregar o perfil salvo; pede para preencher novamente.
      setStep('profile');
    } finally {
      setLoading(false);
    }
  }

  function startEditProfile() {
    if (profile) {
      setDisplayName(profile.displayName ?? '');
      setGender(profile.gender ?? '');
      setDateOfBirth(profile.dateOfBirth ?? '');
      setSelectedAgeRange(profile.dateOfBirth ? calculateAgeRange(profile.dateOfBirth) : null);
      setEducationLevel(profile.educationLevel ?? '');
      setEconomicLevel(profile.economicLevel ?? '');
      setNeighborhood(profile.neighborhood ?? '');
      setCityId(profile.cityId ?? '');
      setCityInputValue(profile.cityName ?? '');
      if (profile.stateId) setSelectedStateId(profile.stateId);
    }
    setStep('profile');
  }

  function handleDateOfBirthChange(date: dayjs.Dayjs | null) {
    if (date) {
      setDateOfBirth(date.format('YYYY-MM-DD'));
      setSelectedAgeRange(calculateAgeRange(date.toDate()));
    } else {
      setDateOfBirth('');
      setSelectedAgeRange(null);
    }
  }

  async function handleVerifyOtp() {
    setError(null);
    setLoading(true);
    try {
      const result = await verifyOtp(challengeId, code);
      if (result.accountConflict) {
        setStep('accountConflict');
        return;
      }
      await proceedAsGuest(result);
    } catch (err: any) {
      setError(err.response?.data?.message ?? 'Código incorreto ou expirado.');
    } finally {
      setLoading(false);
    }
  }

  async function handleRecoverEmail() {
    setError(null);
    setLoading(true);
    try {
      await recoverAccountEmail(challengeId);
      setEmailRecoverySent(true);
    } catch (err: any) {
      setError(err.response?.data?.message ?? 'Erro ao enviar o SMS. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  function handleUnlinkPhone() {
    Modal.confirm({
      title: 'Desvincular este telefone?',
      icon: <ExclamationCircleFilled />,
      content:
        'A conta anterior perderá o vínculo com este telefone e precisará validá-lo novamente para usá-lo. Essa ação não pode ser desfeita.',
      okText: 'Desvincular e continuar',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        setError(null);
        setUnlinkLoading(true);
        try {
          const result = await unlinkAccountPhone(challengeId);
          if (result.accountConflict) {
            // Não deveria acontecer — o telefone acabou de ser desvinculado no backend.
            setError('Não foi possível concluir a desvinculação. Tente novamente.');
            return;
          }
          await proceedAsGuest(result);
        } catch (err: any) {
          setError(err.response?.data?.message ?? 'Erro ao desvincular o telefone. Tente novamente.');
        } finally {
          setUnlinkLoading(false);
        }
      },
    });
  }

  async function handleSubmitProfile() {
    setError(null);
    setLoading(true);
    try {
      const demographics: GuestDemographics = {
        gender: gender as GuestDemographics['gender'],
        dateOfBirth,
        educationLevel: educationLevel as GuestDemographics['educationLevel'],
        economicLevel: economicLevel as GuestDemographics['economicLevel'],
        cityId,
        neighborhood: neighborhood.trim(),
        displayName: displayName.trim() || undefined,
      };
      await saveGuestDemographics(savedToken, demographics);
      navigate(`/surveys/${surveyId}/guest-take`);
    } catch (err: any) {
      setError(err.response?.data?.message ?? 'Erro ao salvar perfil. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }

  const stepIndex =
    step === 'phone' ? 0
    : step === 'otp' || step === 'accountConflict' ? 1
    : 2;
  const demographicsValid = gender && dateOfBirth && educationLevel && economicLevel && cityId && neighborhood.trim();

  const genderLabel = GENDER_OPTIONS.find((o) => o.value === profile?.gender)?.label ?? '—';
  const educationLabel = EDUCATION_OPTIONS.find((o) => o.value === profile?.educationLevel)?.label ?? '—';
  const economicLabel = ECONOMIC_OPTIONS.find((o) => o.value === profile?.economicLevel)?.label ?? '—';
  const ageLabel = profile?.dateOfBirth ? `${dayjs().diff(dayjs(profile.dateOfBirth), 'year')} anos` : '—';

  return (
    <div style={{ maxWidth: 480, margin: '40px auto', padding: '0 16px' }}>
      <Logo onClick={() => navigate('/')} compact />
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Title level={4} style={{ margin: 0 }}>
            <MobileOutlined /> Participar com código por SMS
          </Title>

          <Steps
            current={stepIndex}
            size="small"
            items={[
              { title: 'Telefone' },
              { title: 'Código' },
              { title: 'Perfil' },
            ]}
          />

          {error && <Alert message={error} type="error" showIcon />}

          {step === 'phone' && (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Paragraph type="secondary">
                Informe seu número de telefone para receber um código de verificação por SMS.
              </Paragraph>
              <Form layout="vertical" onFinish={handleSendOtp}>
                <Form.Item label="Telefone" required extra="DDD + número com 8 ou 9 dígitos. Ex: 47 + 99999-9999">
                  <PhoneInput value={phone} onChange={setPhone} />
                </Form.Item>
                <Form.Item>
                  <Checkbox
                    checked={lgpdConsent}
                    onChange={(e) => setLgpdConsent(e.target.checked)}
                  >
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Ao informar meu número, concordo com o uso do telefone para verificação de identidade nesta pesquisa, conforme a LGPD. Estou ciente de que não participarei de todas as vantagens da plataforma, como premiações.
                    </Text>
                  </Checkbox>
                </Form.Item>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    loading={loading}
                    disabled={!PHONE_E164_REGEX.test(phone) || !lgpdConsent}
                    style={{ flex: 1 }}
                  >
                    Enviar código
                  </Button>
                  <Button type="text" size="small" onClick={() => navigate(-1)}>
                    Voltar
                  </Button>
                </div>
              </Form>
              <Alert
                type="warning"
                showIcon
                message="Ao participar com SMS, sem criar uma conta (email) no sistema, você não participa dos prêmios da plataforma. Você recebe o hash de auditoria mas não recebe bilhetes para participar da premiação de engajamento."
              />
            </Space>
          )}

          {step === 'otp' && (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Paragraph type="secondary">
                Insira o código de 6 dígitos enviado para <strong>{phone}</strong>.
              </Paragraph>
              <Form layout="vertical" onFinish={handleVerifyOtp}>
                <Form.Item label="Código de verificação" required>
                  <Input
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    size="large"
                    maxLength={6}
                  />
                </Form.Item>
                <Button type="primary" htmlType="submit" block size="large" loading={loading} disabled={code.length !== 6}>
                  Verificar
                </Button>
                <Button
                  type="link"
                  block
                  style={{ marginTop: 8 }}
                  disabled={resendAvailableAt ? new Date() < resendAvailableAt : false}
                  onClick={() => { setStep('phone'); setCode(''); }}
                >
                  Reenviar código
                </Button>
              </Form>
            </Space>
          )}

          {step === 'accountConflict' && (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Paragraph type="secondary">
                Este número já está vinculado a uma conta PePoll. O que você gostaria de fazer?
              </Paragraph>

              {emailRecoverySent && (
                <Alert
                  type="success"
                  showIcon
                  message="Enviamos um SMS com o e-mail associado a esse número."
                />
              )}

              <Button
                type="primary"
                icon={<LoginOutlined />}
                block
                size="large"
                onClick={() =>
                  navigate('/login', {
                    state: {
                      participationSurveyId: surveyId,
                      message: 'Faça login com sua conta para responder a esta pesquisa.',
                    },
                  })
                }
              >
                Fazer login
              </Button>

              <Button
                icon={<QuestionCircleOutlined />}
                block
                size="large"
                loading={loading}
                disabled={emailRecoverySent}
                onClick={handleRecoverEmail}
              >
                Esqueci meu e-mail de cadastro
              </Button>

              <Button
                danger
                icon={<DisconnectOutlined />}
                block
                size="large"
                loading={unlinkLoading}
                onClick={handleUnlinkPhone}
              >
                Este número é meu, desvincular da conta antiga
              </Button>
            </Space>
          )}

          {step === 'confirmProfile' && (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Paragraph type="secondary">
                Encontramos seu perfil de participações anteriores. Confira se os dados ainda estão corretos.
              </Paragraph>
              <Descriptions column={1} bordered size="small">
                <Descriptions.Item label="Nome">{profile?.displayName || 'Não informado'}</Descriptions.Item>
                <Descriptions.Item label="Gênero">{genderLabel}</Descriptions.Item>
                <Descriptions.Item label="Idade">{ageLabel}</Descriptions.Item>
                <Descriptions.Item label="Nível de Escolaridade">{educationLabel}</Descriptions.Item>
                <Descriptions.Item label="Classe Econômica">{economicLabel}</Descriptions.Item>
                <Descriptions.Item label="Localização">
                  {profile?.cityName ? `${profile.cityName} - ${profile.stateName}` : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Bairro">{profile?.neighborhood || '—'}</Descriptions.Item>
              </Descriptions>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                block
                size="large"
                onClick={() => navigate(`/surveys/${surveyId}/guest-take`)}
              >
                Confirmar e continuar
              </Button>
              <Button icon={<EditOutlined />} block size="large" onClick={startEditProfile}>
                Editar meus dados
              </Button>
            </Space>
          )}

          {step === 'profile' && (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                Complete seu perfil para responder a pesquisa.
              </Paragraph>

              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'flex-start',
                  padding: 16,
                  borderRadius: 8,
                  border: '1px solid #d9d9d9',
                  background: '#fafafa',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    background: 'rgba(46, 43, 129, 0.08)',
                    color: '#2E2B81',
                    flexShrink: 0,
                    fontSize: 16,
                  }}
                >
                  <SolutionOutlined />
                </div>
                <div>
                  <Text strong style={{ display: 'block', color: '#141414', marginBottom: 4 }}>
                    Por que pedimos esses dados?
                  </Text>
                  <Text type="secondary" style={{ fontSize: 14, lineHeight: 1.5714 }}>
                    Gênero, idade, escolaridade, renda e localização compõem o censo da pesquisa: garantem que o
                    resultado reflita a diversidade da população pesquisada. Usados apenas de forma agregada —
                    nunca divulgados nem associados ao seu nome.
                  </Text>
                </div>
              </div>

              <Form layout="vertical" onFinish={handleSubmitProfile}>
                <Form.Item label="Seu nome" extra="Opcional — usado apenas para personalizar sua experiência.">
                  <Input
                    placeholder="Ex: João"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    size="large"
                    maxLength={100}
                  />
                </Form.Item>
                <Form.Item label="Gênero" required>
                  <Select
                    placeholder="Selecione"
                    size="large"
                    value={gender || undefined}
                    onChange={setGender}
                    options={GENDER_OPTIONS}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <Form.Item
                  label="Data de nascimento"
                  required
                  extra={selectedAgeRange ? `Faixa etária: ${getAgeRangeLabel(selectedAgeRange)}` : undefined}
                >
                  <DatePicker
                    placeholder="DD/MM/AAAA"
                    size="large"
                    style={{ width: '100%' }}
                    format={{ format: 'DD/MM/YYYY', type: 'mask' }}
                    value={dateOfBirth ? dayjs(dateOfBirth) : undefined}
                    disabledDate={(d) =>
                      !!d &&
                      (d > dayjs().subtract(MINIMUM_PARTICIPANT_AGE, 'year').endOf('day') ||
                        d < dayjs().subtract(MAXIMUM_PARTICIPANT_AGE, 'year').startOf('day'))
                    }
                    onChange={handleDateOfBirthChange}
                  />
                </Form.Item>
                <Form.Item label="Nível de Escolaridade" required>
                  <Select
                    placeholder="Selecione"
                    size="large"
                    value={educationLevel || undefined}
                    onChange={setEducationLevel}
                    options={EDUCATION_OPTIONS}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <Form.Item
                  label={
                    <span>
                      Classe Econômica <EconomicLevelInfoIcon />
                    </span>
                  }
                  required
                >
                  <Select
                    placeholder="Selecione"
                    size="large"
                    value={economicLevel || undefined}
                    onChange={setEconomicLevel}
                    options={ECONOMIC_OPTIONS}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <Form.Item label="Estado" required>
                  <Select
                    placeholder="Selecione seu estado"
                    size="large"
                    showSearch
                    value={selectedStateId || undefined}
                    onChange={handleStateChange}
                    optionFilterProp="label"
                    options={states.map((s) => ({ value: s.id, label: `${s.name} (${s.code})` }))}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <Form.Item
                  label="Cidade"
                  required
                  extra={!selectedStateId ? 'Selecione um estado primeiro' : 'Digite para buscar sua cidade'}
                >
                  <AutoComplete
                    placeholder={selectedStateId ? 'Digite para buscar cidade' : 'Selecione o estado primeiro'}
                    disabled={!selectedStateId}
                    size="large"
                    value={cityInputValue}
                    onSearch={(text) => { setCitySearchText(text); setCityInputValue(text); setCityId(''); }}
                    onSelect={handleCitySelect}
                    notFoundContent={searchingCities ? 'Buscando...' : 'Nenhuma cidade encontrada'}
                    options={cities.map((c) => ({ value: c.name, label: c.name, id: c.id }))}
                    filterOption={false}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
                <Form.Item label="Bairro" required>
                  <Input
                    placeholder="Digite seu bairro"
                    value={neighborhood}
                    onChange={(e) => setNeighborhood(e.target.value)}
                    size="large"
                    maxLength={100}
                  />
                </Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  size="large"
                  loading={loading}
                  disabled={!demographicsValid}
                >
                  Continuar para a pesquisa
                </Button>
              </Form>
            </Space>
          )}
        </Space>
      </Card>
    </div>
  );
}
